import { useState, useEffect } from 'react';
import { Table, Card, Button, Popconfirm, message, Tag, Space, Input, Dropdown } from 'antd';
import { DeleteOutlined, ReloadOutlined, PrinterOutlined, SearchOutlined, FileTextOutlined, TagOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import DataMatrixImage from '../../components/marking/DataMatrixImage';
import MarkingPrintPreview from '../../components/marking/MarkingPrintPreview';
import MarkingThermalPreview from '../../components/marking/MarkingThermalPreview';
import BrandLogo from '../../components/BrandLogo';
import { markingApi, type MarkingCode } from '../../api/marking';
import useAuthStore from '../../store/authStore';
import useScanBufferStore from '../../store/scanBufferStore';
import useIsMobile from '../../hooks/useIsMobile';

const MarkingBulkPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [scans, setScans] = useState<MarkingCode[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [thermalPreviewOpen, setThermalPreviewOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [search, setSearch] = useState('');
  const pageSize = 20;
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('marking:write');

  const load = async (p = page, order = sortOrder, searchQuery = search) => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        markingApi.listScans((p - 1) * pageSize, pageSize, undefined, order, searchQuery || undefined, 'bulk'),
        markingApi.countScans(undefined, searchQuery || undefined, 'bulk'),
      ]);
      setScans(listRes.data);
      setTotal(countRes.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, sortOrder, search]);

  const handleDelete = async (marking_code: string) => {
    await markingApi.deleteCode(marking_code);
    message.success('Скан удалён');
    setSelectedCodes((codes) => codes.filter((x) => x !== marking_code));
    load();
  };

  const handleDeleteSelected = async () => {
    if (selectedCodes.length === 0) return;
    const results = await Promise.allSettled(
      selectedCodes.map((code) => markingApi.deleteCode(code)),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    if (ok > 0) message.success(`Удалено: ${ok}`);
    if (fail > 0) message.error(`Не удалось удалить: ${fail}`);
    setSelectedCodes([]);
    load();
  };

  const handleThermalPrint = async () => {
    setPrinting(true);
    try {
      await markingApi.printThermal(selectedCodes);
      message.success('Печать запущена');
      setThermalPreviewOpen(false);
    } catch (e) {
      message.error('Ошибка запуска печати');
    } finally {
      setPrinting(false);
    }
  };

  const selectedScans = scans.filter((s) => selectedCodes.includes(s.marking_code));

  const columns: ColumnsType<MarkingCode> = [
    {
      title: 'Товар',
      key: 'product',
      render: (_, r) => r.product_name
        ? (
          <Space size={8}>
            {r.brand && <BrandLogo brand={r.brand} />}
            <span>{r.product_name}</span>
          </Space>
        )
        : <Tag color="orange">GTIN: {r.gtin || '—'}</Tag>,
    },
    {
      title: 'Тара',
      dataIndex: 'container_size',
      width: 100,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Остаток, л',
      key: 'bulk_remaining_volume',
      width: 130,
      render: (_, r) => {
        if (r.total_volume == null) return <Tag color="orange">объём не задан</Tag>;
        const remaining = r.bulk_remaining_volume != null ? Number(r.bulk_remaining_volume) : null;
        const color = r.status === 'written_off' ? 'red' : remaining === 0 ? 'default' : 'geekblue';
        return <Tag color={color}>{remaining != null ? `${remaining} / ${Number(r.total_volume)}` : `? / ${Number(r.total_volume)}`}</Tag>;
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 130,
      render: (v: string) => {
        const map: Record<string, { label: string; color: string }> = {
          ready: { label: 'Готова', color: 'green' },
          reserved: { label: 'Резерв', color: 'purple' },
          sold: { label: 'Продана', color: 'default' },
          off_balance: { label: 'Вне баланса', color: 'orange' },
          written_off: { label: 'Списана', color: 'red' },
        };
        const info = map[v] || { label: v, color: 'default' };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      title: 'Серийный номер',
      dataIndex: 'serial_number',
      width: 160,
      render: (v: string | null) => <code>{v || '—'}</code>,
    },
    {
      title: 'Дата',
      dataIndex: 'scanned_at',
      width: 150,
      sorter: true,
      sortOrder: sortOrder === 'asc' ? 'ascend' : 'descend',
      sortDirections: ['ascend', 'descend'],
      render: (v: string) => new Date(v).toLocaleString('ru-RU'),
    },
    {
      title: 'Кто',
      dataIndex: 'scanned_by_name',
      width: 140,
    },
    ...(canWrite ? [{
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, r: MarkingCode) => (
        <Popconfirm title="Удалить скан?" onConfirm={() => handleDelete(r.marking_code)}>
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <Card
      title={
        <Space>
          <span>Разливной товар</span>
          <Input
            placeholder="Поиск..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ width: 200 }}
            allowClear
          />
        </Space>
      }
      extra={
        <Space>
          <Dropdown
            disabled={selectedCodes.length === 0}
            menu={{
              items: [
                { key: 'sheet', label: 'Лист (А4)', icon: <FileTextOutlined /> },
                { key: 'thermal', label: 'Марки (20×20 мм)', icon: <TagOutlined /> },
              ],
              onClick: ({ key }) => {
                if (key === 'sheet') setPreviewOpen(true);
                if (key === 'thermal') setThermalPreviewOpen(true);
              },
            }}
            trigger={['click']}
          >
            <Button icon={<PrinterOutlined />} disabled={selectedCodes.length === 0}>
              Печать{selectedCodes.length > 0 ? ` (${selectedCodes.length})` : ''}
            </Button>
          </Dropdown>
          {canWrite && (
            <Popconfirm
              title="Удалить выбранные?"
              description={`Будет удалено ${selectedCodes.length} скан(ов). Отмена невозможна.`}
              onConfirm={handleDeleteSelected}
              disabled={selectedCodes.length === 0}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={selectedCodes.length === 0}
              >
                Удалить{selectedCodes.length > 0 ? ` (${selectedCodes.length})` : ''}
              </Button>
            </Popconfirm>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => load()}>Обновить</Button>
        </Space>
      }
    >
      <Table
        rowKey="marking_code"
        columns={columns}
        dataSource={scans}
        loading={loading}
        rowSelection={{
          selectedRowKeys: selectedCodes,
          onChange: (keys) => setSelectedCodes(keys as string[]),
        }}
        onChange={(_pag, _flt, sorter) => {
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          // AntD Table имеет 3-state цикл сортировки по умолчанию:
          //   null → ascend → descend → null → ...
          // При текущем descend клик возвращает sorter.order=undefined
          // (cleared) — интерпретируем как «переключить на противоположное».
          let next: 'asc' | 'desc';
          if (s?.order === 'ascend') next = 'asc';
          else if (s?.order === 'descend') next = 'desc';
          else next = sortOrder === 'desc' ? 'asc' : 'desc';
          if (next !== sortOrder) {
            setPage(1);
            setSortOrder(next);
          }
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: setPage,
          showSizeChanger: false,
          showTotal: (t) => `Всего: ${t}`,
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if ((e.target as HTMLElement).closest('.ant-table-selection-column, button, .ant-popover')) return;
            setExpandedKeys((prev) => prev.includes(record.marking_code) ? [] : [record.marking_code]);
          },
          style: { cursor: 'pointer' },
        })}
        expandable={{
          expandedRowKeys: expandedKeys,
          showExpandColumn: false,
          expandedRowRender: (record) => (
            <div style={{ padding: '16px 0' }}>
              <div style={isMobile ? {
                position: 'sticky', left: '50vw', transform: 'translateX(-50%)',
                display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              } : {
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              }}>
                <DataMatrixImage code={record.raw_code} scale={3} />
                <div>
                  <Tag>GTIN: {record.gtin || '—'}</Tag>
                  <Tag>S/N: {record.serial_number || '—'}</Tag>
                </div>
              </div>
            </div>
          ),
        }}
        size="small"
      />
      <MarkingPrintPreview
        open={previewOpen}
        scans={selectedScans}
        onClose={async () => {
          setPreviewOpen(false);
          // Sync buffer count, но сканы уже resolved=bulk, так что счётчик не изменится
          useScanBufferStore.getState().refreshCount();
        }}
      />
      <MarkingThermalPreview
        open={thermalPreviewOpen}
        scans={selectedScans}
        onClose={() => setThermalPreviewOpen(false)}
        onPrint={handleThermalPrint}
        printing={printing}
      />
    </Card>
  );
};

export default MarkingBulkPage;
